import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Pause, RotateCcw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const StrawBreathing = () => {
  const navigate = useNavigate();
  const [isActive, setIsActive] = useState(false);
  const [phase, setPhase] = useState('ready'); // ready, inhale, exhale
  const [count, setCount] = useState(0);
  const [cycles, setCycles] = useState(0);
  const intervalRef = useRef(null);

  const INHALE_DURATION = 4;
  const EXHALE_DURATION = 8;

  useEffect(() => {
    if (isActive) {
      intervalRef.current = setInterval(() => {
        setCount(prev => {
          if (phase === 'inhale') {
            if (prev >= INHALE_DURATION) {
              setPhase('exhale');
              return 1;
            }
            return prev + 1;
          } else if (phase === 'exhale') {
            if (prev >= EXHALE_DURATION) {
              setPhase('inhale');
              setCycles(c => c + 1);
              return 1;
            }
            return prev + 1;
          }
          return prev;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isActive, phase]);

  const startBreathing = () => {
    if (phase === 'ready') {
      setPhase('inhale');
      setCount(1);
    }
    setIsActive(true);
  };

  const pauseBreathing = () => {
    setIsActive(false);
  };

  const resetBreathing = () => {
    setIsActive(false);
    setPhase('ready');
    setCount(0);
    setCycles(0);
  };

  const getProgress = () => {
    if (phase === 'inhale') {
      return (count / INHALE_DURATION) * 100;
    } else if (phase === 'exhale') {
      return (count / EXHALE_DURATION) * 100;
    }
    return 0;
  };

  const getInstruction = () => {
    if (phase === 'ready') return 'Press play to begin';
    if (phase === 'inhale') return 'Breathe in through your nose';
    if (phase === 'exhale') return 'Exhale slowly through pursed lips';
    return '';
  };

  const getSubInstruction = () => {
    if (phase === 'inhale') return 'Feel your belly expand';
    if (phase === 'exhale') return 'Like blowing through a straw';
    return '2-to-1 breathing for calm';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#E8F5E9] via-[#C8E6C9] to-[#A5D6A7] relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-green-400 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-teal-400 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-8">
        <header className="flex justify-between items-center mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-white text-[#2D3748] px-6 py-3 rounded-full font-semibold hover:bg-slate-100 transition-all duration-300 flex items-center gap-2 shadow-sm"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          
          <h1 className="text-3xl md:text-4xl font-bold text-[#2D3748]">Straw Breathing</h1>
          
          <div className="w-24"></div>
        </header>

        {/* Info Card */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 mb-8 text-center">
          <p className="text-[#4A5568] font-medium">
            2-to-1 Breathing (Pursed-Lip Breathing) • Excellent for hyperventilation
          </p>
        </div>

        {/* Main Breathing Circle */}
        <div className="flex flex-col items-center justify-center">
          <div className="relative w-80 h-80 mb-8">
            {/* Outer ring - progress */}
            <svg className="w-full h-full transform -rotate-90">
              <circle
                cx="160"
                cy="160"
                r="150"
                fill="none"
                stroke="#E0E0E0"
                strokeWidth="8"
              />
              <motion.circle
                cx="160"
                cy="160"
                r="150"
                fill="none"
                stroke={phase === 'inhale' ? '#4CAF50' : '#26A69A'}
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={2 * Math.PI * 150}
                animate={{ strokeDashoffset: 2 * Math.PI * 150 * (1 - getProgress() / 100) }}
                transition={{ duration: 0.5 }}
              />
            </svg>

            {/* Center content */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <AnimatePresence mode="wait">
                <motion.div
                  key={phase}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.8, opacity: 0 }}
                  className="text-center"
                >
                  {/* Straw visualization */}
                  <div className="mb-4">
                    {phase === 'inhale' ? (
                      <motion.div
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 1, repeat: Infinity }}
                        className="text-6xl"
                      >
                        👃
                      </motion.div>
                    ) : phase === 'exhale' ? (
                      <div className="relative">
                        <motion.div
                          animate={{ scale: [1, 0.9, 1] }}
                          transition={{ duration: 1, repeat: Infinity }}
                          className="text-6xl"
                        >
                          😙
                        </motion.div>
                        {/* Animated breath/air bubbles */}
                        <motion.div
                          animate={{ 
                            x: [0, 30, 60],
                            opacity: [1, 0.5, 0],
                            scale: [0.5, 1, 1.5]
                          }}
                          transition={{ duration: 2, repeat: Infinity }}
                          className="absolute top-1/2 left-full text-2xl"
                        >
                          💨
                        </motion.div>
                      </div>
                    ) : (
                      <div className="text-6xl">🥤</div>
                    )}
                  </div>

                  <div className="text-5xl font-bold text-[#2D3748] mb-2">
                    {phase === 'ready' ? '–' : count}
                  </div>
                  <div className="text-xl font-semibold text-[#4A5568] uppercase tracking-wide">
                    {phase === 'ready' ? 'Ready' : phase}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Instructions */}
          <motion.div
            key={phase}
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-center mb-8"
          >
            <p className="text-2xl font-semibold text-[#2D3748] mb-2">
              {getInstruction()}
            </p>
            <p className="text-lg text-[#4A5568]">
              {getSubInstruction()}
            </p>
          </motion.div>

          {/* Controls */}
          <div className="flex gap-4 mb-8">
            {!isActive ? (
              <button
                onClick={startBreathing}
                className="bg-gradient-to-r from-green-500 to-teal-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:-translate-y-1 transition-all duration-300 shadow-lg flex items-center gap-2"
              >
                <Play className="w-6 h-6" />
                {phase === 'ready' ? 'Start' : 'Resume'}
              </button>
            ) : (
              <button
                onClick={pauseBreathing}
                className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:-translate-y-1 transition-all duration-300 shadow-lg flex items-center gap-2"
              >
                <Pause className="w-6 h-6" />
                Pause
              </button>
            )}
            
            <button
              onClick={resetBreathing}
              className="bg-white text-[#4A5568] px-6 py-4 rounded-full font-semibold hover:bg-slate-100 transition-all duration-300 shadow-sm flex items-center gap-2"
            >
              <RotateCcw className="w-5 h-5" />
              Reset
            </button>
          </div>

          {/* Cycle counter */}
          <div className="bg-white rounded-2xl px-8 py-4 shadow-sm">
            <p className="text-[#4A5568] text-center">
              Completed cycles: <span className="font-bold text-[#2D3748]">{cycles}</span>
            </p>
          </div>

          {/* Pattern info */}
          <div className="mt-8 bg-white/60 backdrop-blur-sm rounded-2xl p-6 max-w-md">
            <h3 className="font-bold text-[#2D3748] mb-3 text-center">The Pattern</h3>
            <div className="flex justify-center gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-2">
                  <span className="text-2xl font-bold text-green-600">4</span>
                </div>
                <p className="text-sm text-[#4A5568]">Inhale</p>
                <p className="text-xs text-[#718096]">through nose</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-2">
                  <span className="text-2xl font-bold text-teal-600">8</span>
                </div>
                <p className="text-sm text-[#4A5568]">Exhale</p>
                <p className="text-xs text-[#718096]">pursed lips</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StrawBreathing;
